import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";
import type { Rfq, RfqImage, RfqStatus } from "../rfqs/api.js";

/** Admin view of an attached image — the API presigns a GET URL for each. */
export type AdminRfqImage = RfqImage & { url: string; expiresInSeconds: number };

/** Customer contact info merged onto admin RFQ detail responses. */
export interface AdminCustomer {
  sub: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  phoneNumber?: string;
}

/** Admin note appended by an admin while triaging an RFQ. */
export interface AdminNote {
  id: string;
  authorSub: string;
  text: string;
  createdAt: string;
}

export type AdminRfq = Omit<Rfq, "images"> & {
  images?: AdminRfqImage[];
  adminNotes?: AdminNote[];
  customer?: AdminCustomer;
};

export interface AdminRfqsPage {
  items: AdminRfq[];
  nextToken?: string;
}

/** Status values supported by the admin list endpoint. "OPEN" is a synthetic
 * view that merges SUBMITTED + IN_REVIEW on the server. */
export type AdminListStatus = RfqStatus | "OPEN";

const QK = {
  list: (status: AdminListStatus) => ["admin", "rfqs", { status }] as const,
  detail: (customerSub: string, id: string) => ["admin", "rfqs", customerSub, id] as const,
};

export function useAdminRfqsList(status: AdminListStatus, nextToken?: string) {
  return useQuery({
    queryKey: nextToken ? ([...QK.list(status), { nextToken }] as const) : QK.list(status),
    queryFn: () => {
      const qs = new URLSearchParams({ status });
      if (nextToken) qs.set("nextToken", nextToken);
      return apiFetch<AdminRfqsPage>(`/admin/rfqs?${qs.toString()}`);
    },
  });
}

export function useAdminRfq(customerSub: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: customerSub && id ? QK.detail(customerSub, id) : (["admin", "rfqs", "noop"] as const),
    queryFn: () =>
      apiFetch<AdminRfq>(
        `/admin/rfqs/${encodeURIComponent(customerSub!)}/${encodeURIComponent(id!)}`,
      ),
    enabled: !!customerSub && !!id,
  });
}

export type AdminAssignableStatus = "IN_REVIEW" | "CLOSED";

export function useAdminUpdateStatus(customerSub: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: AdminAssignableStatus) =>
      apiFetch<AdminRfq>(
        `/admin/rfqs/${encodeURIComponent(customerSub)}/${encodeURIComponent(id)}/status`,
        { method: "PATCH", json: { status } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "rfqs"] });
    },
  });
}

export function useAdminAddNote(customerSub: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      apiFetch<AdminRfq>(
        `/admin/rfqs/${encodeURIComponent(customerSub)}/${encodeURIComponent(id)}/notes`,
        { method: "POST", json: { text } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.detail(customerSub, id) });
    },
  });
}
